import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { ResumoCompradores } from '@/features/proposta/entities/ResumoProposta/resumo-compradores-proposta'
import { ResumoParticipacoesImobiliarias } from '@/features/proposta/entities/ResumoProposta/resumo-participacoes-imobiliarias'
import { ResumoSimulacoes } from '@/features/proposta/entities/ResumoProposta/resumo-simulacoes-proposta'
import { useCriarProposta } from '@/features/proposta/hooks/use-criar-proposta'
import type { TPropostaSchemaInput, TPropostaSchemaOutput } from '@/features/proposta/schemas'
import { CompradoresSection } from '@/pages/proposta/resumo-proposta/components/compradores-section'
import { DetalhesPropostaSection } from '@/pages/proposta/resumo-proposta/components/detalhes-proposta-section'
import { PropostaCompraSection } from '@/pages/proposta/resumo-proposta/components/proposta-compra-section'
import { DestructiveActionGuard } from '@/shared/components/destructive-action-guard'
import { Button } from '@/shared/components/ui'
import { useGuardedHandler } from '@/shared/hooks/use-guarded-handlers'
import { checkOnlineGuard } from '@/shared/utils'

export default function ResumoProposta() {
  const { getValues, setValue, handleSubmit } = useFormContext<TPropostaSchemaInput, unknown, TPropostaSchemaOutput>()
  const { t } = useTranslation('proposta')
  const { mutateAsync, isPending } = useCriarProposta()
  const navigate = useNavigate()

  const {
    informacoesIniciais: { vagaIds, planta, simulacoesAcessorios, simulacaoUnidade, simulacaoLote },
    detalhesDaProposta: {
      imobiliariasCorretores,
      assinaturaContrato,
      termosDaProposta,
      participacaoCompradores,
      observacoes,
    },
    cadastroCompradores,
  } = getValues()

  const resumoSimulacoes = ResumoSimulacoes.from(simulacaoUnidade, simulacaoLote, simulacoesAcessorios)
  const resumoCompradores = new ResumoCompradores(cadastroCompradores, participacaoCompradores)
  const resumoParticipacao = ResumoParticipacoesImobiliarias.from(imobiliariasCorretores)

  const onEdit = (step: number) => {
    setValue('step', step)
  }

  const handleEnviarPropostaParaAprovacao = useGuardedHandler(
    [checkOnlineGuard()],
    async (formOutput: TPropostaSchemaOutput) => {
      const response = await mutateAsync(formOutput)
      navigate(`/proposta/${response.data.numero}`, { replace: true })
    },
  )

  return (
    <div className="bg-foreground font-poppins mx-auto max-w-6xl space-y-4 p-6 pb-16 md:mb-0 md:pb-6">
      <div className="grid gap-1">
        <h1 className="text-base text-neutral-800">{t('resumo_proposta.titulo')}</h1>
        <p className="text-sm text-neutral-500">{t('resumo_proposta.subtitulo')}</p>
      </div>
      <PropostaCompraSection
        onEdit={onEdit}
        resumo={resumoSimulacoes}
        plantaDescricao={planta.descricao}
        vagaIds={vagaIds}
      />
      <CompradoresSection onEdit={onEdit} resumo={resumoCompradores} />
      <DetalhesPropostaSection
        onEdit={onEdit}
        resumoParticipacao={resumoParticipacao}
        assinaturaContrato={assinaturaContrato}
        observacoes={observacoes}
        termosDaProposta={termosDaProposta}
      />
      <div className="fixed right-0 bottom-0 flex w-full justify-center border-none bg-white px-6 py-4 inset-shadow-xs md:static md:justify-end">
        <DestructiveActionGuard
          showCloseButton
          title={t('resumo_proposta.modal_envio.titulo')}
          description={t('resumo_proposta.modal_envio.descricao')}
        >
          <Button
            disabled={isPending}
            className="font-nunito h-12 w-full md:w-48.5"
            variant="default"
            onClick={handleSubmit(handleEnviarPropostaParaAprovacao)}
            loadingText={t('resumo_proposta.carregando_envio')}
            isLoading={isPending}
          >
            {t('resumo_proposta.enviar_aprovacao')}
          </Button>
        </DestructiveActionGuard>
      </div>
    </div>
  )
}
